import { useState, useMemo, useEffect } from 'react';
import { ArrowLeft, Clock, Map as MapIcon, Image as ImageIcon } from 'lucide-react';
import { db } from '../firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { appId } from '../constants';
import Avatar from './Avatar';
import MobileSocialMap from './MobileSocialMap';

const SocialFeedView = ({ user, allUsers, navigate, b2Token, isMobile }) => {
  const [sortBy, setSortBy] = useState('newest_added'); // 'newest_added', 'oldest_added', 'newest_photo', 'oldest_photo'
  const [fetchedPhotos, setFetchedPhotos] = useState([]);
  const [fetchedAudio, setFetchedAudio] = useState([]);
  const [activeTab, setActiveTab] = useState('feed'); // 'feed' or 'map'
  const [mediaFilter, setMediaFilter] = useState('all'); // 'all', 'photos', 'music'
  
  const searchParams = new URLSearchParams(window.location.search);
  const targetUserId = searchParams.get('user');

  const getAuthorizedUrl = (url) => {
    if (!url) return url;
    let fixedUrl = url.replace('f005.backblazeb2.com', 'f000.backblazeb2.com');
    if (fixedUrl.includes('backblazeb2.com') && !b2Token) return null;
    if (fixedUrl.includes('backblazeb2.com') && b2Token) {
      return `${fixedUrl}?Authorization=${b2Token}`;
    }
    return fixedUrl;
  };

  useEffect(() => {
    const qPhotos = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'photos'),
      where('isSocial', '==', true)
    );
    const unsubPhotos = onSnapshot(qPhotos, (snap) => {
      const docs = snap.docs.map((d) => ({ ...d.data(), id: d.id, isPhoto: true }));
      setFetchedPhotos(docs);
    });

    const qAudio = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'media'),
      where('isSocial', '==', true),
      where('type', '==', 'audio')
    );
    const unsubAudio = onSnapshot(qAudio, (snap) => {
      const docs = snap.docs.map((d) => ({ ...d.data(), id: d.id, isAudio: true }));
      setFetchedAudio(docs);
    });

    return () => {
      unsubPhotos();
      unsubAudio();
    };
  }, []);

  const socialPhotos = useMemo(() => {
    let list = [...fetchedPhotos, ...fetchedAudio];
    if (targetUserId) {
      list = list.filter(p => p.userId === targetUserId || p.createdBy === targetUserId);
    }
    if (mediaFilter === 'photos') {
      list = list.filter(p => p.isPhoto);
    } else if (mediaFilter === 'music') {
      list = list.filter(p => p.isAudio);
    }
    
    list.sort((a, b) => {
      const aTime = a.socialPostedAt?.seconds || a.createdAt?.seconds || 0;
      const bTime = b.socialPostedAt?.seconds || b.createdAt?.seconds || 0;
      
      if (sortBy === 'newest_added') {
        return bTime - aTime;
      } else if (sortBy === 'oldest_added') {
        return aTime - bTime;
      }
      // Assuming 'newest_photo' could use EXIF data later, but for now we just fallback to createdAt
      return bTime - aTime;
    });
    return list;
  }, [fetchedPhotos, fetchedAudio, targetUserId, sortBy, mediaFilter]);

  return (
    <div className="flex flex-col h-full bg-zinc-50 overflow-y-auto custom-scrollbar">
      <div className="sticky top-0 z-10 bg-white border-b border-zinc-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate('/')}
            className="p-1.5 hover:bg-zinc-100 rounded-lg transition-colors text-zinc-500"
          >
            <ArrowLeft size={18} strokeWidth={2.5} />
          </button>
          
          <div className="flex items-center gap-1 bg-zinc-100 p-1 rounded-xl">
            <button
              onClick={() => setActiveTab('feed')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'feed' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'}`}
            >
              <ImageIcon size={14} strokeWidth={2.5} /> Feed
            </button>
            <button
              onClick={() => setActiveTab('map')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'map' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'}`}
            >
              <MapIcon size={14} strokeWidth={2.5} /> Map
            </button>
          </div>
        </div>
        
        {activeTab === 'feed' && (
          <div className="flex items-center gap-2">
            <select 
              value={mediaFilter}
              onChange={(e) => setMediaFilter(e.target.value)}
              className="bg-zinc-100 border-none outline-none text-[10px] font-bold uppercase tracking-widest text-zinc-600 rounded px-2 py-1.5 cursor-pointer hover:bg-zinc-200 transition-colors"
            >
              <option value="all">Everything</option>
              <option value="photos">Photos Only</option>
              <option value="music">Music Only</option>
            </select>
            <select 
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-zinc-100 border-none outline-none text-[10px] font-bold uppercase tracking-widest text-zinc-600 rounded px-2 py-1.5 cursor-pointer hover:bg-zinc-200 transition-colors"
            >
              <option value="newest_added">Newest Added</option>
              <option value="oldest_added">Oldest Added</option>
              <option value="newest_photo">Newest Photo</option>
              <option value="oldest_photo">Oldest Photo</option>
            </select>
          </div>
        )}
      </div>

      <div className="flex-1 max-w-xl mx-auto w-full pb-20">
        {targetUserId && allUsers[targetUserId] && (
          <div className="flex flex-col items-center p-8 bg-white border-b border-zinc-200 gap-4 shadow-sm mb-4">
            <Avatar user={allUsers[targetUserId]} className="w-24 h-24 text-4xl shadow-md" />
            <div className="text-center max-w-sm">
              <h1 className="text-2xl font-black text-zinc-900">{allUsers[targetUserId].username}</h1>
              {allUsers[targetUserId].bio && <p className="text-sm font-medium text-zinc-600 mt-2">{allUsers[targetUserId].bio}</p>}
            </div>
            
            {allUsers[targetUserId].socialLinks && allUsers[targetUserId].socialLinks.length > 0 && (
              <div className="flex flex-col w-full gap-3 mt-4">
                {allUsers[targetUserId].socialLinks.map((link, idx) => {
                  // Ensure URL has protocol
                  const url = link.url.startsWith('http') ? link.url : `https://${link.url}`;
                  return (
                    <a 
                      key={idx} 
                      href={url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="w-full bg-zinc-900 hover:bg-black text-white font-black uppercase tracking-widest text-center py-3.5 rounded-xl transition-all shadow-sm active:scale-[0.98] text-xs"
                    >
                      {link.title}
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'feed' ? (
          socialPhotos.length > 0 ? (
          <div className="flex flex-col gap-8 py-4 px-4">
            {socialPhotos.map(photo => {
              const postTime = photo.socialPostedAt?.seconds || photo.createdAt?.seconds;
              const locationLabel = [photo.locationName, photo.locationCity].filter(Boolean).join(', ');
              const mapsQuery = [photo.locationName, photo.locationAddress || photo.locationCity].filter(Boolean).join(' ');
              const mapsUrl = locationLabel
                ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsQuery)}${photo.locationPlaceId ? `&query_place_id=${photo.locationPlaceId}` : ''}`
                : null;
              return (
                <div key={photo.id} className="bg-white rounded-2xl shadow-sm border border-zinc-200 overflow-hidden">
                  <div className="p-3 flex items-center justify-between border-b border-zinc-100 bg-white">
                    <div className="flex items-center gap-1.5 text-zinc-800">
                      {photo.isPhoto ? (
                        <>
                          <MapIcon size={14} className="text-zinc-400" />
                          {mapsUrl ? (
                            <a
                              href={mapsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-xs font-bold hover:text-blue-600 focus-visible:text-blue-600 focus-visible:outline-none focus-visible:underline"
                            >
                              {locationLabel}
                            </a>
                          ) : (
                            <span className="text-xs font-bold">Add Location...</span>
                          )}
                        </>
                      ) : (
                        <>
                          <span className="text-xs font-bold">{photo.title || photo.name}</span>
                        </>
                      )}
                    </div>
                    {postTime && (
                      <span className="text-[10px] font-black uppercase tracking-widest text-zinc-400">
                        {new Date(postTime * 1000).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                      </span>
                    )}
                  </div>
                  
                  {photo.isAudio ? (
                    <div className="p-6 bg-zinc-50 flex items-center justify-center">
                      <audio controls src={getAuthorizedUrl(photo.url)} className="w-full max-w-sm" />
                    </div>
                  ) : (
                    <div className="relative aspect-square sm:aspect-auto sm:max-h-[60vh] bg-zinc-100">
                      <img 
                        src={getAuthorizedUrl(photo.url)} 
                        alt={photo.name}
                        className="w-full h-full object-cover sm:object-contain"
                        loading="lazy"
                      />
                    </div>
                  )}
                  
                  <div className="p-4 bg-white border-t border-zinc-100 flex flex-col gap-2">
                    {photo.caption ? (
                      <p className="text-sm font-medium text-zinc-800">{photo.caption}</p>
                    ) : (
                      <p className="text-sm font-medium text-zinc-400 italic">No caption...</p>
                    )}
                    {photo.tags && photo.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {photo.tags.map(t => (
                          <span key={t} className="text-blue-500 text-xs font-bold">#{t}</span>
                        ))}
                      </div>
                    )}
                    {photo.isPhoto && (
                      <div className="mt-1 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-zinc-400">
                        <Clock size={10} />
                        <span>Taken {photo.exif?.DateTimeOriginal ? new Date(photo.exif.DateTimeOriginal).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '?'}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full py-20">
            <div className="w-8 h-8 border-2 border-zinc-200 border-t-zinc-400 rounded-full animate-spin"></div>
          </div>
        )
      ) : (
        <MobileSocialMap allUsers={allUsers} />
      )}
      </div>
    </div>
  );
};

export default SocialFeedView;
